import { useState } from "react";
import { IonItem, IonList, IonRadio, IonRadioGroup } from "@ionic/react";
import { Button, Colors, Icon } from "swing-components";

import { translate } from "~utils";

import { LocationFilterValueTypes, RadiusFilterType } from "../FilterBar/types";

// Types

type RadiusActionSheetProps = {
  onDismiss: () => void;
  onApply: (values: LocationFilterValueTypes) => void;
  currentValues: LocationFilterValueTypes;
};

// Styles

const styles = {
  container: {
    position: "relative",
    overflow: "scroll",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    position: "sticky",
    top: 0,
    padding: "32px 16px 16px 16px",
    background: "white",
    display: "flex",
    justifyContent: "space-between",
    zIndex: 999,
  },
  content: {
    padding: "0 16px",
    flex: 1,
  },
  footer: {
    position: "sticky",
    bottom: 0,
    padding: 16,
    background: "white",
    display: "flex",
    gap: 16,
  },
} as const;

// Helpers

const radiusOptions: Array<{ label: string; value: RadiusFilterType }> = [
  { label: translate("LOCATION_FILTER_MILES", { miles: 5 }), value: 5 },
  { label: translate("LOCATION_FILTER_MILES", { miles: 10 }), value: 10 },
  { label: translate("LOCATION_FILTER_MILES", { miles: 20 }), value: 20 },
  { label: translate("LOCATION_FILTER_MILES", { miles: 30 }), value: 30 },
  { label: translate("LOCATION_FILTER_MILES", { miles: 60 }), value: 60 },
  { label: translate("LOCATION_FILTER_MILES", { miles: "61+" }), value: 80 },
];

// Main Component

export function RadiusActionSheet(props: RadiusActionSheetProps) {
  const { onDismiss, onApply, currentValues } = props;

  const [selectedRadius, setSelectedRadius] = useState<RadiusFilterType>(currentValues.radius);

  const hasChanged = selectedRadius !== currentValues.radius;

  const handleApply = () => {
    onApply({
      ...currentValues,
      radius: selectedRadius,
    });
    onDismiss();
  };

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <span css={{ fontSize: 20 }}>{translate("LOCATION_FILTER_DISTANCE")}</span>
        <div css={{ cursor: "pointer" }} onClick={onDismiss}>
          <Icon name="Close" color={Colors.blue500} />
        </div>
      </div>

      <div css={styles.content}>
        <IonRadioGroup
          value={selectedRadius}
          onIonChange={(e) => setSelectedRadius(Number(e.detail.value) as RadiusFilterType)}
        >
          <IonList css={{ "--ion-item-background": "transparent" }}>
            {radiusOptions.map((option, index) => (
              <IonItem
                key={option.value}
                lines={index === radiusOptions.length - 1 ? "none" : undefined}
                css={{ "--padding-start": 0 }}
              >
                <IonRadio value={option.value} labelPlacement="end" justify="start">
                  <span css={{ fontSize: 16 }}>{option.label}</span>
                </IonRadio>
              </IonItem>
            ))}
          </IonList>
        </IonRadioGroup>
      </div>

      <div css={styles.footer}>
        <div css={{ flex: 1 }}>
          <Button type="tertiary" onClick={onDismiss} isFluid>
            {translate("LABEL_CANCEL")}
          </Button>
        </div>
        <div css={{ flex: 1 }}>
          <Button
            status={hasChanged ? "enabled" : "disabled"}
            onClick={handleApply}
            type="primary"
            isFluid
          >
            {translate("LOCATION_MODAL_APPLY_BUTTON")}
          </Button>
        </div>
      </div>
    </div>
  );
}
